import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Mic } from "lucide-react";

const NAV_ITEMS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/episodes", label: "Episodes" },
  { to: "/book", label: "Book A Slot" },
  { to: "/be-a-guest", label: "Be A Guest" },
  { to: "/merch", label: "Merch" },
  { to: "/media", label: "Media" },
  { to: "/sponsorship", label: "Sponsor" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-[#0A0A0A]/95 backdrop-blur-md border-[#262626]"
          : "bg-[#0A0A0A]/70 backdrop-blur-sm border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-2 group"
          >
            <span className="w-9 h-9 bg-[#E10600] flex items-center justify-center group-hover:bg-[#1E6FE0] transition-colors">
              <Mic className="w-5 h-5 text-white" />
            </span>
            <span className="font-display text-2xl tracking-tight text-white leading-none">
              PROVE <span className="text-[#1E6FE0]">YOUR</span> <span className="text-[#E10600]">POINT</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-xs tracking-[0.15em] uppercase font-bold transition-colors whitespace-nowrap ${
                    isActive
                      ? "text-[#E10600]"
                      : "text-zinc-400 hover:text-white"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/be-a-guest"
              data-testid="nav-cta"
              className="ml-2 btn-primary"
            >
              Apply To Be A Guest
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="nav-mobile-menu"
          className="lg:hidden bg-[#0A0A0A] border-t border-[#262626]"
        >
          <div className="px-4 py-6 space-y-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `block px-4 py-3 text-sm tracking-[0.2em] uppercase font-bold border-l-2 transition-colors ${
                    isActive
                      ? "border-[#E10600] text-[#E10600] bg-[#121212]"
                      : "border-transparent text-zinc-300 hover:text-white hover:bg-[#121212]"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/be-a-guest"
              data-testid="nav-mobile-cta"
              className="block mt-4 mx-4 text-center btn-primary justify-center"
            >
              Apply To Be A Guest
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
